import React, {useCallback, useEffect, useRef, useState} from "react";
import axios from "axios";
import {OverlayTrigger, Tooltip} from "react-bootstrap";
import {PiWarningBold} from "react-icons/pi";
import {Button, FlexToast, useExternalControls} from "laravel-inertia-backend";
import HeaderWithHelp from "../SalesAnalysis/HeaderWithHelp.jsx";
import {formatElapsedClock, formatDurationWords, friendlyErrorMessage} from "../SalesAnalysis/format.js";
import {formatDatumZeit} from "./format.js";
import {COSMOS_PLANUNG_HINWEIS} from "../Stock/bestandTexte.js";
import {TOOLTIP_POPPER_CONFIG} from "./tooltipConfig.js";

const POLL_INTERVAL_MS = 4000;
const BESTAND_VERALTET_STUNDEN = 26;

/** True, wenn ein Zeitpunkt länger als die Grenze zurückliegt - Rückfall, solange `bestand_stand.veraltet` vom Backend fehlt. */
function istVeraltet(zeitpunktIso, grenzeStunden = BESTAND_VERALTET_STUNDEN) {
    if (!zeitpunktIso) {
        return false;
    }
    const differenzMs = Date.now() - new Date(zeitpunktIso).getTime();
    return Number.isFinite(differenzMs) && differenzMs > grenzeStunden * 3600 * 1000;
}

const NEU_BERECHNEN_HILFE =
    "Baut den Wochenbedarf, die Kennzahlen und das Prognose-Protokoll für den gesamten Bereich neu auf. " +
    "Dauert rund 40 Sekunden und läuft im Hintergrund; bis dahin zeigt die Seite den bisherigen Stand. " +
    "Nötig nur nach einer Änderung an Annahmen, Ausschlüssen oder Lieferungen - sonst läuft es ohnehin " +
    "jede Nacht automatisch um 05:37 und 05:53 Uhr.";

/**
 * Datenstand-Leiste der Lieferfähigkeit mit Knopf "Neu berechnen" - Muster
 * Components/SalesAnalysis/DataStatusBar.jsx, angepasst an
 * CosmosNeuBerechnenJob (kein "aggregated_at"). Pollt
 * GET /cosmos/daten/berechnung-status, kein Seiten-Neuladen, Rückmeldung
 * per Toast; `onFertig` lädt die Tabelle nach.
 */
export default function CosmosDatenstandLeiste({datenstand, canRecalculate, onFertig}) {
    const {addToast} = useExternalControls();
    const [status, setStatus] = useState(null);
    const [elapsed, setElapsed] = useState(0);
    const pollRef = useRef(null);
    const tickRef = useRef(null);
    const cancelledRef = useRef(false);
    const knownFinishedAtRef = useRef(undefined);
    const knownErrorRef = useRef(undefined);

    const stopTick = () => {
        if (tickRef.current) {
            clearInterval(tickRef.current);
            tickRef.current = null;
        }
    };

    const startTick = (sinceIso) => {
        stopTick();
        const since = sinceIso ? new Date(sinceIso).getTime() : Date.now();
        const update = () => setElapsed(Math.max(0, (Date.now() - since) / 1000));
        update();
        tickRef.current = setInterval(update, 1000);
    };

    const fetchStatus = useCallback(
        (initial = false) => {
            axios
                .get("/cosmos/daten/berechnung-status")
                .then(({data}) => {
                    if (cancelledRef.current) {
                        return;
                    }
                    setStatus(data);

                    if (initial) {
                        knownFinishedAtRef.current = data.finished_at || null;
                        knownErrorRef.current = data.last_error || null;
                    }

                    if (data.running && !data.stale) {
                        startTick(data.started_at || data.queued_at);
                        pollRef.current = setTimeout(() => fetchStatus(false), POLL_INTERVAL_MS);
                        return;
                    }

                    stopTick();

                    if (!initial && data.finished_at && data.finished_at !== knownFinishedAtRef.current) {
                        knownFinishedAtRef.current = data.finished_at;
                        onFertig?.();
                        const dauer = formatDurationWords(data.last_duration_s);
                        addToast(
                            <FlexToast
                                variant="success"
                                title="Cosmos"
                                autohide
                                message={dauer ? `Die Neuberechnung ist abgeschlossen (${dauer}).` : "Die Neuberechnung ist abgeschlossen."}
                            />
                        );
                    }

                    if (!initial && data.last_error && data.last_error !== knownErrorRef.current) {
                        knownErrorRef.current = data.last_error;
                        addToast(<FlexToast variant="danger" title="Cosmos" autohide={false} message={data.last_error} />);
                    }
                })
                .catch(() => {
                    if (cancelledRef.current) {
                        return;
                    }
                    stopTick();
                });
        },
        [addToast, onFertig]
    );

    useEffect(() => {
        cancelledRef.current = false;
        fetchStatus(true);
        return () => {
            cancelledRef.current = true;
            if (pollRef.current) {
                clearTimeout(pollRef.current);
            }
            stopTick();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const starten = () => {
        axios
            .post("/cosmos/daten/neu-berechnen")
            .then(() => fetchStatus(false))
            .catch((e) => {
                addToast(
                    <FlexToast
                        variant="danger"
                        title="Cosmos"
                        autohide={e.response?.status === 403}
                        message={friendlyErrorMessage(e, "Die Neuberechnung konnte nicht gestartet werden.")}
                    />
                );
            });
    };

    const isRunning = Boolean(status?.running);
    const buttonDisabled = isRunning && !status?.stale;

    // Bestand-Stand (Sichtprüfung 1, Befund 30): `bestand_stand` mit
    // fertigem Text kommt künftig vom Backend (K3); bis dahin - und als
    // Netz, falls das Feld einmal fehlt - baut sich der Text aus dem
    // bestehenden `bestand`-Feld zusammen, "veraltet" wird clientseitig aus
    // dem Zeitstempel abgeleitet.
    const bestandStand = datenstand?.bestand_stand;
    const bestandZeitpunkt = bestandStand?.zeitpunkt ?? datenstand?.bestand?.wert ?? null;
    const bestandText = bestandStand?.text ?? (datenstand?.bestand?.wert ? `Bestand ${formatDatumZeit(datenstand.bestand.wert)}` : null);
    const bestandVeraltet = bestandStand?.veraltet ?? istVeraltet(bestandZeitpunkt);

    return (
        <div className="d-flex align-items-center flex-wrap gap-3 small text-muted mb-3">
            {bestandText && <HeaderWithHelp help={bestandStand?.quelle ?? datenstand?.bestand?.quelle}>{bestandText}</HeaderWithHelp>}
            {bestandVeraltet && (
                <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG}
                    placement="top"
                    overlay={
                        <Tooltip id="cosmos-bestand-veraltet-tooltip">
                            Reichweiten und Warnungen auf dieser Seite beruhen so lange auf dem letzten bekannten Stand.
                        </Tooltip>
                    }
                >
                    <span className="badge text-bg-warning d-inline-flex align-items-center gap-1 text-wrap text-start">
                        <PiWarningBold className="flex-shrink-0" aria-hidden="true" />
                        Bestand ist älter als {BESTAND_VERALTET_STUNDEN} Stunden – der stündliche Abgleich läuft nicht.
                    </span>
                </OverlayTrigger>
            )}
            {datenstand?.offene_kundenmenge?.wert && (
                <HeaderWithHelp help={`${datenstand.offene_kundenmenge.quelle}. ${COSMOS_PLANUNG_HINWEIS}`}>
                    Offen, nicht reserviert (Planung) {formatDatumZeit(datenstand.offene_kundenmenge.wert)}
                </HeaderWithHelp>
            )}
            {datenstand?.verkaeufe?.wert && (
                <HeaderWithHelp help={datenstand.verkaeufe.quelle}>Verkäufe {formatDatumZeit(datenstand.verkaeufe.wert)}</HeaderWithHelp>
            )}
            {datenstand?.letzte_berechnung?.wert && (
                <HeaderWithHelp help={datenstand.letzte_berechnung.quelle}>
                    Zuletzt berechnet {formatDatumZeit(datenstand.letzte_berechnung.wert)}
                </HeaderWithHelp>
            )}

            {canRecalculate && (
                <div className="ms-auto d-flex flex-column align-items-end gap-1">
                    <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG} placement="left" overlay={<Tooltip id="cosmos-recalculate-tooltip">{NEU_BERECHNEN_HILFE}</Tooltip>}>
                        <span>
                            <Button size="sm" variant="outline-secondary" disabled={buttonDisabled} onClick={starten}>
                                {isRunning ? `Wird berechnet … (${formatElapsedClock(elapsed)})` : "Neu berechnen"}
                            </Button>
                        </span>
                    </OverlayTrigger>
                    {isRunning && status?.waiting_for_worker && (
                        <div className="text-warning" style={{maxWidth: 260}}>
                            Der Hintergrunddienst hat den Auftrag noch nicht übernommen.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
